/**
 * Cookie service utility for reading and writing cookies
 * Uses js-cookie library
 */

import Cookies from 'js-cookie';

export interface CookieOptions {
  domain?: string;
  expires?: Date;
  path?: string;
  sameSite?: 'Strict' | 'Lax' | 'None';
  secure?: boolean;
}

/**
 * Get a cookie value by name
 */
export function getCookie(name: string): string | null {
  if (typeof document === 'undefined') {
    return null;
  }

  const value = Cookies.get(name);
  return value ?? null;
}

/**
 * Set a cookie with options
 */
export function setCookie(
  name: string,
  value: string,
  options: CookieOptions = {}
): void {
  if (typeof document === 'undefined') {
    return;
  }

  // Convert CookieOptions to js-cookie CookieAttributes
  const cookieAttributes: Cookies.CookieAttributes = {};

  if (options.path) {
    cookieAttributes.path = options.path;
  }

  if (options.domain) {
    cookieAttributes.domain = options.domain;
  }

  if (options.expires) {
    cookieAttributes.expires = options.expires;
  }

  if (options.sameSite) {
    // js-cookie uses lowercase for sameSite
    cookieAttributes.sameSite = options.sameSite.toLowerCase() as
      | 'strict'
      | 'lax'
      | 'none';
  }

  if (options.secure) {
    cookieAttributes.secure = options.secure;
  }

  Cookies.set(name, value, cookieAttributes);
}

/**
 * Delete a cookie
 */
export function deleteCookie(
  name: string,
  options: Pick<CookieOptions, 'domain' | 'path'> = {}
): void {
  if (typeof document === 'undefined') {
    return;
  }

  const cookieAttributes: Cookies.CookieAttributes = {};

  if (options.path) {
    cookieAttributes.path = options.path;
  }

  if (options.domain) {
    cookieAttributes.domain = options.domain;
  }

  Cookies.remove(name, cookieAttributes);
}
